import { useCallback, useEffect, useState } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useEcpStoresFromCache } from '../../../../../graphql/ecp-locator/actions/ecp-stores';
import {
  filterParams,
  setUserLocation,
  sortingTypeExt,
} from '../../../../../graphql/ecp-locator/ecp-locator.cache';
import { ecpLocatorApi } from '../../../../../rest/ecp-locator';
import { useSetFilterParams } from '../../../../../graphql/ecp-locator/actions/set-filter-params';
import { useEcpUserLocation } from '../../../../../graphql/ecp-locator/actions/user-location';
import { DEFAULT_RANGE } from '../../../../../constants/ecp-locator';
import { filteringByStores } from '../../../../../utils/ecp-locator';
import { EcpStoreType } from '../../../../../rest/ecp-locator';
import { retailerShopTypeItems } from '../../ecp-filters-modal';
import { UpdateStores } from '../../../../../models';

export const useUpdateEcpStores = () => {
  const { setEcpStoresToCache } = useEcpStoresFromCache();
  const { setFilterParamsToCache } = useSetFilterParams();
  const { setEcpUserLocationToCache } = useEcpUserLocation();
  const filtersCurrent = useReactiveVar(filterParams);
  const userLocationCurrent = useReactiveVar(setUserLocation);
  const [isStoresLoading, setLoadingState] = useState(false);
  const [isStoresError, setErrorState] = useState(false);
  const currentSortingType = useReactiveVar(sortingTypeExt);

  const updateStores = useCallback(async ({ filters, userLocation }: UpdateStores) => {
    let filtersApplied = filtersCurrent;
    let userLocationApplied = userLocationCurrent;

    if (filters) {
      filtersApplied = filters;
    }

    if (userLocation) {
      userLocationApplied = userLocation;
    }

    const { specializations, services, shops } = filtersApplied;
    const { latitude, longitude } = userLocationApplied;
    const isC2cStores = services === EcpStoreType.c2cActivated;

    try {
      setLoadingState(true);
      const { data } = await ecpLocatorApi.getStores({
        lat: latitude,
        long: longitude,
        range: DEFAULT_RANGE,
        sorting: currentSortingType,
        speciality: specializations.join(','),
        type: isC2cStores ? '' : services || '',
        c2cActivated: isC2cStores,
      });

      if (filters) {
        setFilterParamsToCache(filters);
      }

      if (userLocation) {
        setEcpUserLocationToCache(userLocation);
      }

      setEcpStoresToCache(filteringByStores(data, shops, retailerShopTypeItems.length));
    } catch {
      setErrorState(true);
    }
    setLoadingState(false);
  }, [
    filtersCurrent,
    userLocationCurrent,
    currentSortingType,
    setEcpStoresToCache,
    setEcpUserLocationToCache,
    setFilterParamsToCache,
  ]);

  useEffect(() => () => {
    setLoadingState(false);
  }, []);

  return { updateStores, isStoresLoading, isStoresError };
};
